/**
 * One roster across however many programs a group covers.
 *
 * `staff_camp_roster` answers for a single program, so a merged after-school group asks it
 * once per program and joins the answers here. The RPC does not carry school or room —
 * both live in `students.metadata` — so those are fetched once for the whole group rather
 * than per program.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from './supabase';
import { selectRosterEnrollments } from '../utils/rosterEnrollmentWindow';
import { isSampleId, sampleRosterEntries } from './sample/sampleData';
import { compareByFirstName } from '../utils/rosterGrouping';
import { getTodaySessionDate } from '../utils/schedule';
import type { TodayProgram } from './useTodaysPrograms';

/** Row returned by the staff_camp_roster RPC. */
interface RosterRpcRow {
  enrollment_id: string;
  student_id: string;
  first_name: string;
  last_name: string;
  grade: string | null;
  via_summer_pass: boolean;
  has_allergies: boolean;
  allergies_details: string | null;
  lunch_opted: boolean;
  selected_days: string[] | null;
  /**
   * This month's Flex plan. `null` means no plan row on file — which is not the same as
   * `[]`, a plan that deliberately selected no days.
   */
  flex_dates: string[] | null;
  /** Whether this child holds a BHCS after-school seat covering the roster day. */
  in_after_school: boolean;
}

export interface RosterEntry {
  enrollmentId: string;
  studentId: string;
  /** "First Last" — how a name is displayed. */
  studentName: string;
  /**
   * "Last, First" — the reversed form, kept so a teacher who types a surname first still
   * finds the child. Not the sort key: see `compareByFirstName`.
   */
  sortName: string;
  grade: string | null;
  /** From `students.metadata->>'school'`; the RPC does not carry it. */
  school: string | null;
  /** From `students.metadata->>'room_number'`; digits-only, kept as a string. */
  roomNumber: string | null;
  /**
   * Object path of the student's headshot in the private `student-photos` bucket, or null
   * when none is on file. Fetched alongside school and room; the RPC does not carry it.
   */
  photoPath: string | null;
  viaSummerPass: boolean;
  hasAllergies: boolean;
  allergiesDetails: string | null;
  /** `per_day` billing: the dates this enrollment bought. */
  selectedDays: string[] | null;
  /** `flex_monthly` billing: this month's confirmed days, or null when unplanned. */
  flexDates: string[] | null;
  /**
   * Whether this child holds an active BHCS after-school seat whose term covers today.
   *
   * Only meaningful on an enrichment roster, and only there is it rendered. Chess, tennis
   * and piano run for an hour inside the after-school day for most of the children in them
   * and as a standalone class for the rest, and the two end the hour differently: one walks
   * back to after-school, the other is collected by an adult who is already outside. The
   * teacher cannot tell which from a name, and the mix is real — this term's chess rosters
   * are 6-of-9, 6-of-8 and 1-of-4.
   *
   * Answered by `staff_camp_roster` rather than here: an enrichment teacher is scoped to
   * their own class, so a client-side read of the after-school enrollments returns nothing
   * under RLS. Kaleido seats do not count, per BHCS-122 — a Kaleido after-school place is
   * not somewhere a BHCS enrichment teacher may send a child at 4pm.
   */
  inAfterSchool: boolean;
  /** Which program this row's attendance is recorded against. */
  programId: string;
  programName: string;
  billingType: string;
}

interface StudentMetadataRow {
  id: string;
  metadata: { school?: string; room_number?: string } | null;
  photo_path: string | null;
}

/** The enrollment's own term, which the roster RPC does not carry. */
interface EnrollmentTermRow {
  id: string;
  enrollment_start_date: string | null;
  enrollment_end_date: string | null;
}

/**
 * The entries whose own enrollment covers `today` — the same question the admin pickup
 * board asks, answered by the same `selectRosterEnrollments` rule, so the two boards cannot
 * name different children.
 *
 * An enrollment with no readable term row — a sample child, or a Full Summer Pass held in a
 * program this teacher is not scoped to — is kept: an unknown term is not evidence that a
 * child is unexpected, and dropping a name is the one failure a pickup roster must not have.
 *
 * Exported for testing.
 */
export function expectedEntries(
  entries: readonly RosterEntry[],
  terms: readonly EnrollmentTermRow[],
  today: string,
): RosterEntry[] {
  const termById = new Map(terms.map(t => [t.id, t]));

  // A finished term is finished: it is left out of the selection below, whose "nothing has
  // started yet" fallback is about a term that has not begun.
  const notEnded = entries.flatMap(entry => {
    const term = termById.get(entry.enrollmentId);
    if (!term?.enrollment_start_date) return [];
    if (term.enrollment_end_date && term.enrollment_end_date < today) return [];
    return [{ id: entry.enrollmentId, enrollment_start_date: term.enrollment_start_date }];
  });

  const { enrollments } = selectRosterEnrollments(notEnded, today);
  const expected = new Set(enrollments.map(e => e.id));
  return entries.filter(entry => {
    const term = termById.get(entry.enrollmentId);
    if (!term?.enrollment_start_date) return true;
    return expected.has(entry.enrollmentId);
  });
}

/**
 * The date argument for a roster RPC: omitted for today, set for any other day.
 *
 * Both RPCs default `p_date` to today, so leaving it off for the day-of roster keeps that
 * call identical to what it has always been — the live check-in screen does not depend on
 * the database having the date-aware signature. Only the preview of a future session asks
 * for a day explicitly.
 */
function rosterDateArg(sessionDate: string): { p_date?: string } {
  return sessionDate === getTodaySessionDate() ? {} : { p_date: sessionDate };
}

export const staffRosterKeys = {
  byPrograms: (programIds: readonly string[], sessionDate: string) =>
    ['staff-roster', sessionDate, ...[...programIds].sort()] as const,
};

export function useStaffRosterEntries(programs: readonly TodayProgram[], sessionDate: string) {
  const programIds = programs.map(p => p.id);

  return useQuery<RosterEntry[]>({
    queryKey: staffRosterKeys.byPrograms(programIds, sessionDate),
    queryFn: async () => {
      const perProgram = await Promise.all(
        programs.map(async (program): Promise<RosterEntry[]> => {
          const common = {
            programId: program.id,
            programName: program.name,
            billingType: program.billing_type,
          };

          // A sample program's roster is invented in the browser; no RPC is issued for it.
          if (isSampleId(program.id)) {
            return sampleRosterEntries(program.id, sessionDate).map(entry => {
              const cut = entry.studentName.lastIndexOf(' ');
              return {
                ...entry,
                sortName:
                  cut > 0
                    ? `${entry.studentName.slice(cut + 1)}, ${entry.studentName.slice(0, cut)}`
                    : entry.studentName,
                flexDates: null,
                // Sample children are invented in the browser and have no bucket object;
                // they show initials, exactly as a real student without a headshot does.
                photoPath: null,
                ...common,
              };
            });
          }

          const { data, error } = await supabase.rpc('staff_camp_roster', {
            p_program_id: program.id,
            ...rosterDateArg(sessionDate),
          });
          if (error) throw error;

          return ((data ?? []) as RosterRpcRow[]).map(row => ({
            enrollmentId: row.enrollment_id,
            studentId: row.student_id,
            studentName: `${row.first_name} ${row.last_name}`,
            sortName: `${row.last_name}, ${row.first_name}`,
            grade: row.grade,
            school: null,
            roomNumber: null,
            photoPath: null,
            viaSummerPass: row.via_summer_pass,
            hasAllergies: row.has_allergies,
            allergiesDetails: row.allergies_details,
            selectedDays: row.selected_days,
            flexDates: row.flex_dates ?? null,
            inAfterSchool: row.in_after_school,
            ...common,
          }));
        }),
      );

      // Deduped by enrollment: a Full Summer Pass holder is returned by every camp week
      // their pass spans, and merging those weeks must not list them twice. A student with
      // two genuine enrollments keeps both rows — each has its own attendance to record.
      const byEnrollment = new Map<string, RosterEntry>();
      for (const entry of perProgram.flat()) {
        if (!byEnrollment.has(entry.enrollmentId)) byEnrollment.set(entry.enrollmentId, entry);
      }
      const entries = Array.from(byEnrollment.values());
      if (entries.length === 0) return [];

      const realStudentIds = Array.from(
        new Set(entries.map(e => e.studentId).filter(id => !isSampleId(id))),
      );
      const realEnrollmentIds = Array.from(
        new Set(entries.map(e => e.enrollmentId).filter(id => !isSampleId(id))),
      );

      // Two lookups the RPC does not answer, in parallel: where the student comes from, and
      // when their own enrollment runs — the program's dates are not the family's, since a
      // school that starts later buys a later start against the same program. Teachers can
      // read both under RLS: students via `staff_has_enrollment_access`, enrollments via
      // their program scope.
      const [studentRes, termRes] = await Promise.all([
        realStudentIds.length > 0
          ? supabase.from('students').select('id, metadata, photo_path').in('id', realStudentIds)
          : Promise.resolve({ data: [], error: null }),
        realEnrollmentIds.length > 0
          ? supabase
              .from('enrollments')
              .select('id, enrollment_start_date, enrollment_end_date')
              .in('id', realEnrollmentIds)
          : Promise.resolve({ data: [], error: null }),
      ]);
      if (studentRes.error) throw studentRes.error;
      if (termRes.error) throw termRes.error;

      const studentById = new Map(
        ((studentRes.data ?? []) as StudentMetadataRow[]).map(s => [s.id, s]),
      );
      for (const entry of entries) {
        const student = studentById.get(entry.studentId);
        if (!student) continue;
        entry.school = student.metadata?.school ?? null;
        entry.roomNumber = student.metadata?.room_number ?? null;
        entry.photoPath = student.photo_path;
      }

      const expected = expectedEntries(
        entries,
        (termRes.data ?? []) as EnrollmentTermRow[],
        sessionDate,
      );

      // Sorted before grouping: `groupBySchoolThenGrade` preserves input order inside a
      // grade, so this one call is what orders every grade on the screen.
      return expected.sort(compareByFirstName);
    },
    enabled: programs.length > 0,
  });
}

/** Row returned by the `staff_virtual_class_roster` RPC. */
interface VirtualRosterRpcRow {
  enrollment_id: string;
  student_id: string;
  /** The child's after-school program — what this class's attendance is recorded against. */
  program_id: string;
  first_name: string;
  last_name: string;
  grade: string | null;
  school: string | null;
  room_number: string | null;
  photo_path: string | null;
  has_allergies: boolean;
  allergies_details: string | null;
  flex_dates: string[] | null;
  enrollment_start_date: string | null;
  enrollment_end_date: string | null;
}

export const virtualRosterKeys = {
  byClass: (classKey: string, sessionDate: string) => ['staff-virtual-roster', classKey, sessionDate] as const,
};

/**
 * One virtual class's roster — the after-school children in that grade.
 *
 * A separate hook rather than a branch inside `useStaffRosterEntries` because almost none
 * of that function applies here: there is no per-program fan-out (membership is a grade,
 * not an enrollment list), no Full Summer Pass dedup, and no follow-up lookups. The RPC
 * returns school, room, photo and term itself precisely so a teacher who holds only
 * virtual classes needs no direct read of `students` or `enrollments`.
 *
 * `seats` supplies `billingType`, which the RPC does not carry: it is read off the
 * after-school program each child's row points at. That is what makes `flexDayStatus` work
 * unchanged, so a Flexible child shows the same not-scheduled badge here as on the
 * after-school roster.
 *
 * The rows are then put through `expectedEntries`, exactly as the after-school roster is.
 * The RPC's membership test is active enrollment INTERSECT grade, which says nothing about
 * *when* that enrollment runs — so without this a child whose family bought a September
 * start was on the Chinese roster through the whole of August, marked absent by default
 * every session, while the after-school roster for the same children correctly left them
 * off. The term dates this needs are already on the RPC's own rows.
 */
export function useVirtualClassRoster(
  classKey: string | null,
  seats: readonly TodayProgram[],
  sessionDate: string,
) {
  const billingByProgram = new Map(seats.map(p => [p.id, p.billing_type]));
  const label = seats.length > 0 ? 'After School' : '';

  return useQuery<RosterEntry[]>({
    queryKey: virtualRosterKeys.byClass(classKey ?? '', sessionDate),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('staff_virtual_class_roster', {
        p_class_key: classKey,
        ...rosterDateArg(sessionDate),
      });
      if (error) throw error;

      const rows = (data ?? []) as VirtualRosterRpcRow[];
      const entries = rows.map(row => ({
        enrollmentId: row.enrollment_id,
        studentId: row.student_id,
        studentName: `${row.first_name} ${row.last_name}`,
        sortName: `${row.last_name}, ${row.first_name}`,
        grade: row.grade,
        school: row.school,
        roomNumber: row.room_number,
        photoPath: row.photo_path,
        viaSummerPass: false,
        hasAllergies: row.has_allergies,
        allergiesDetails: row.allergies_details,
        // `selected_days` is a per_day billing concept; no after-school program uses it.
        selectedDays: null,
        flexDates: row.flex_dates ?? null,
        // Every child on a virtual roster holds the after-school seat the class is
        // recorded against — membership in one IS an after-school enrollment. Nothing
        // renders the badge here anyway; a Chinese tab is not an enrichment tab.
        inAfterSchool: true,
        programId: row.program_id,
        programName: label,
        billingType: billingByProgram.get(row.program_id) ?? '',
      }));

      // The same rule, from the same module, as the after-school roster and the admin
      // pickup board — so a child cannot be expected in Chinese and not expected at
      // dismissal on the same afternoon.
      return expectedEntries(
        entries,
        rows.map(row => ({
          id: row.enrollment_id,
          enrollment_start_date: row.enrollment_start_date,
          enrollment_end_date: row.enrollment_end_date,
        })),
        sessionDate,
      );
    },
    // The RPC orders by surname; this re-sorts so a Chinese or Math class reads in the same
    // order as the after-school roster the same children appear on.
    select: entries => [...entries].sort(compareByFirstName),
    enabled: Boolean(classKey) && Boolean(sessionDate),
  });
}

/** Where a Flex student stands on a given day. `null` for anyone not billed that way. */
export type FlexDayStatus = 'scheduled' | 'not_scheduled' | 'unplanned';

export function flexDayStatus(entry: RosterEntry, date: string): FlexDayStatus | null {
  if (entry.billingType !== 'flex_monthly') return null;
  // No plan on file is a gap in the data, not a statement about the child. Saying
  // "not scheduled" here would send a teacher home with a student still in the yard.
  if (entry.flexDates === null) return 'unplanned';
  return entry.flexDates.includes(date) ? 'scheduled' : 'not_scheduled';
}
